import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';

// Instância axios sem interceptors de auth
const publicApi = axios.create({ baseURL: API_URL });

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.webp,.pdf';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

function ColetaPublica() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | form | submitted | invalid | expired
  const [reason, setReason] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    nome: '', cpf: '', telefone: '', endereco: '',
    data_preferida: '', hora_preferida: '', convenio: '',
    plano: '', medico_solicitante: '', observacoes: '',
  });
  const [arquivos, setArquivos] = useState([]); // { file, preview }
  const fileInputRef = useRef(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const { data } = await publicApi.get(`/coleta-publica/${token}`);
        if (data.valid) {
          setStatus('form');
        } else {
          if (data.reason === 'Link expirado') setStatus('expired');
          else if (data.status && data.status !== 'rascunho') setStatus('submitted');
          else setStatus('invalid');
          setReason(data.reason || 'Link inválido');
        }
      } catch {
        setStatus('invalid');
        setReason('Link não encontrado');
      }
    };
    validate();
  }, [token]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      arquivos.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
    };
  }, [arquivos]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const novos = [];

    for (const file of files) {
      if (arquivos.length + novos.length >= MAX_FILES) {
        alert(`Máximo de ${MAX_FILES} arquivos permitidos.`);
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`O arquivo "${file.name}" excede o limite de 10MB.`);
        continue;
      }
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      novos.push({ file, preview });
    }

    setArquivos(prev => [...prev, ...novos]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeArquivo = (index) => {
    setArquivos(prev => {
      const removed = prev[index];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone || !form.endereco || !form.data_preferida || !form.hora_preferida) {
      alert('Preencha os campos obrigatórios: Nome, Telefone, Endereço, Data e Hora');
      return;
    }
    setSalvando(true);
    try {
      const formData = new FormData();
      // Campos do formulário
      Object.entries(form).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      // Arquivos
      arquivos.forEach(a => formData.append('anexos', a.file));

      const { data } = await publicApi.post(`/coleta-publica/${token}`, formData);
      if (data.success) {
        setStatus('submitted');
        setReason('');
      } else {
        alert(data.reason || 'Erro ao enviar solicitação');
      }
    } catch (err) {
      alert(err.response?.data?.reason || 'Erro ao enviar solicitação');
    } finally {
      setSalvando(false);
    }
  };

  const s = {
    page: { minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Inter', 'Roboto', sans-serif" },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '32px', width: '100%', maxWidth: '560px' },
    logo: { textAlign: 'center', marginBottom: '24px' },
    logoText: { fontSize: '1.3rem', fontWeight: '700', color: '#1a1a2e' },
    logoSub: { fontSize: '0.85rem', color: '#666', marginTop: '4px' },
    title: { fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' },
    subtitle: { fontSize: '0.85rem', color: '#666', margin: '0 0 20px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    group: { display: 'flex', flexDirection: 'column', gap: '4px' },
    label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
    input: { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', outline: 'none', transition: 'border-color 0.2s' },
    textarea: { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', minHeight: '70px', resize: 'vertical', outline: 'none' },
    btn: { padding: '12px 24px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', width: '100%', marginTop: '16px' },
    msgBox: { textAlign: 'center', padding: '40px 20px' },
    msgIcon: { fontSize: '3rem', marginBottom: '12px' },
    msgTitle: { fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px' },
    msgText: { fontSize: '0.9rem', color: '#666', margin: 0 },
    // Upload styles
    uploadArea: { border: '2px dashed #d1d5db', borderRadius: '8px', padding: '16px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s', backgroundColor: '#fafafa' },
    uploadLabel: { fontSize: '0.85rem', color: '#6b7280', margin: 0 },
    uploadHint: { fontSize: '0.75rem', color: '#9ca3af', margin: '4px 0 0' },
    previewGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' },
    previewItem: { position: 'relative', width: '80px', height: '80px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    previewImg: { width: '100%', height: '100%', objectFit: 'cover' },
    previewPdf: { fontSize: '0.65rem', color: '#6b7280', textAlign: 'center', padding: '4px', wordBreak: 'break-all' },
    removeBtn: { position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '11px', lineHeight: '18px', textAlign: 'center', padding: 0 },
  };

  if (status === 'loading') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.msgBox}>
            <div style={s.msgIcon}>...</div>
            <p style={s.msgTitle}>Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invalid' || status === 'expired') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>
            <div style={s.logoText}>Laboratório Santa Rita</div>
            <div style={s.logoSub}>Coleta Domiciliar</div>
          </div>
          <div style={s.msgBox}>
            <div style={s.msgIcon}>{status === 'expired' ? '⏰' : '❌'}</div>
            <p style={s.msgTitle}>{status === 'expired' ? 'Link Expirado' : 'Link Inválido'}</p>
            <p style={s.msgText}>{reason || 'Este link não é válido. Solicite um novo link ao laboratório.'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>
            <div style={s.logoText}>Laboratório Santa Rita</div>
            <div style={s.logoSub}>Coleta Domiciliar</div>
          </div>
          <div style={s.msgBox}>
            <div style={s.msgIcon}>✅</div>
            <p style={s.msgTitle}>Solicitação Enviada!</p>
            <p style={s.msgText}>Sua solicitação de coleta domiciliar foi recebida com sucesso. Nossa equipe entrará em contato para confirmar o agendamento.</p>
          </div>
        </div>
      </div>
    );
  }

  // status === 'form'
  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.logoText}>Laboratório Santa Rita</div>
          <div style={s.logoSub}>Coleta Domiciliar</div>
        </div>
        <h2 style={s.title}>Solicitar Coleta Domiciliar</h2>
        <p style={s.subtitle}>Preencha os dados abaixo para solicitar uma coleta em domicílio.</p>

        <form onSubmit={handleSubmit}>
          <div style={s.grid}>
            <div style={{ ...s.group, gridColumn: '1 / -1' }}>
              <label style={s.label}>Nome Completo *</label>
              <input style={s.input} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
            </div>
            <div style={s.group}>
              <label style={s.label}>CPF</label>
              <input style={s.input} value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" />
            </div>
            <div style={s.group}>
              <label style={s.label}>Telefone *</label>
              <input style={s.input} value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(00) 00000-0000" required />
            </div>
            <div style={{ ...s.group, gridColumn: '1 / -1' }}>
              <label style={s.label}>Endereço Completo *</label>
              <input style={s.input} value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} placeholder="Rua, número, bairro, cidade" required />
            </div>
            <div style={s.group}>
              <label style={s.label}>Data Preferida *</label>
              <input type="date" style={s.input} value={form.data_preferida} onChange={(e) => setForm({ ...form, data_preferida: e.target.value })} required />
            </div>
            <div style={s.group}>
              <label style={s.label}>Hora Preferida *</label>
              <input type="time" style={s.input} value={form.hora_preferida} onChange={(e) => setForm({ ...form, hora_preferida: e.target.value })} required />
            </div>
            <div style={s.group}>
              <label style={s.label}>Convênio</label>
              <input style={s.input} value={form.convenio} onChange={(e) => setForm({ ...form, convenio: e.target.value })} />
            </div>
            <div style={s.group}>
              <label style={s.label}>Plano</label>
              <input style={s.input} value={form.plano} onChange={(e) => setForm({ ...form, plano: e.target.value })} />
            </div>
            <div style={{ ...s.group, gridColumn: '1 / -1' }}>
              <label style={s.label}>Médico Solicitante</label>
              <input style={s.input} value={form.medico_solicitante} onChange={(e) => setForm({ ...form, medico_solicitante: e.target.value })} />
            </div>
            <div style={{ ...s.group, gridColumn: '1 / -1' }}>
              <label style={s.label}>Observações</label>
              <textarea style={s.textarea} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Informações adicionais sobre a coleta..." />
            </div>

            {/* Upload de pedido médico */}
            <div style={{ ...s.group, gridColumn: '1 / -1' }}>
              <label style={s.label}>Pedido Médico (foto ou PDF)</label>
              <div
                style={s.uploadArea}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#d1d5db';
                  const dt = e.dataTransfer;
                  if (dt.files) {
                    const fakeEvent = { target: { files: dt.files } };
                    handleFileChange(fakeEvent);
                  }
                }}
              >
                <p style={s.uploadLabel}>Clique ou arraste para anexar arquivos</p>
                <p style={s.uploadHint}>JPG, PNG, WebP ou PDF - Máx. {MAX_FILES} arquivos, 10MB cada</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>

              {arquivos.length > 0 && (
                <div style={s.previewGrid}>
                  {arquivos.map((a, i) => (
                    <div key={i} style={s.previewItem}>
                      {a.preview ? (
                        <img src={a.preview} alt={a.file.name} style={s.previewImg} />
                      ) : (
                        <div style={s.previewPdf}>
                          <div style={{ fontSize: '1.4rem', marginBottom: '2px' }}>PDF</div>
                          <div>{a.file.name.substring(0, 12)}</div>
                        </div>
                      )}
                      <button type="button" style={s.removeBtn} onClick={() => removeArquivo(i)} title="Remover">&times;</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button type="submit" style={{ ...s.btn, opacity: salvando ? 0.7 : 1 }} disabled={salvando}>
            {salvando ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ColetaPublica;
